import { useFormContext } from '@calendly/form'
import { useEffect, useRef, useState } from 'react'

import { pathBelongsToFields } from './use_active_tab_validation'

const getErrorPaths = (error) => {
  const validationErrors = error.inner?.length ? error.inner : [error]

  return validationErrors.map((validationError) => validationError.path).filter(Boolean)
}

export const useTabValidityTracking = ({ activeTabKey, tabFieldNamesByKey, validationSchema }) => {
  const { errors, values } = useFormContext()
  const previousTabKeyRef = useRef(activeTabKey)
  const [invalidTabKeys, setInvalidTabKeys] = useState(() => new Set())

  useEffect(() => {
    const leftTabKey = previousTabKeyRef.current
    if (leftTabKey === activeTabKey) return
    previousTabKeyRef.current = activeTabKey

    const fieldNames = tabFieldNamesByKey[leftTabKey] || []
    if (!fieldNames.length) return

    // Some fields (e.g. the meeting name/slug) are validated imperatively via
    // setFieldError rather than through validationSchema, so a passing schema
    // validation alone can't be trusted to mean "no errors" — check the form's
    // live errors too.
    const hasLiveFieldError = fieldNames.some((fieldName) => Boolean(errors[fieldName]))

    const applyResult = (hasSchemaError) => {
      const hasError = hasLiveFieldError || hasSchemaError

      setInvalidTabKeys((previous) => {
        const alreadyFlagged = previous.has(leftTabKey)
        if (hasError === alreadyFlagged) return previous

        const next = new Set(previous)
        if (hasError) {
          next.add(leftTabKey)
        } else {
          next.delete(leftTabKey)
        }
        return next
      })
    }

    if (!validationSchema) {
      applyResult(false)
      return
    }

    validationSchema.validate(values, { abortEarly: false })
      .then(() => applyResult(false))
      .catch((error) => {
        applyResult(getErrorPaths(error).some((path) => pathBelongsToFields(path, fieldNames)))
      })
  }, [activeTabKey, errors, tabFieldNamesByKey, validationSchema, values])

  return invalidTabKeys
}
